import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useState, memo, useRef } from 'react'

import type { RowClickedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { DeviceType } from '~/types/device-type'
import type { IExternalLink } from '~/types/external-links'

import columns from './columns'
import ManageExternalLinkModal from './ManageExternalLinkModal'

import ContentLayout from '~/components/ContentLayout'
import Header from '~/components/Header/Classic'
import Layout from '~/components/Layouts/Layout'
import ModalDialog from '~/components/ModalDialog'
import TableWrapper from '~/components/Tables/TableWrapper'
import ViewTitle from '~/components/ViewTitle'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useAppSummary } from '~/providers/AppSummary'
import { appsKeys } from '~/queries/applications'
import { useDeleteExternalLinkMutation, useExternalLinksListQuery } from '~/queries/external-links'
import { transformByDeviceType } from '~/utils/application'
import { isFocusedCellActions } from '~/utils/table'
import { displayToast } from '~/utils/toast'

const ExternalLinks = (): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const gridRef = useRef<AgGridReact<IExternalLink>>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const [isCreateOrEditModalOpen, setCreateOrEditModalOpen] = useState(false)

  const [externalLinkToDelete, setExternalLinkToDelete] = useState<string>()

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const [externalLinkToEdit, setExternalLinkToEdit] = useState<{ name: string; url: string } | undefined>()

  const [externalLinkIdToEdit, setExternalLinkIdToEdit] = useState('')

  /** Queries & Mutations */

  const { appDetails, appKey, deviceType, isLoading } = useAppSummary()

  const { data: externalLinks, refetch } = useExternalLinksListQuery(appKey, deviceType)

  const { mutateAsync: deleteExternalLink } = useDeleteExternalLinkMutation()

  /** Effects */

  useDocumentTitle('External links')

  /** Handlers */

  const handleRowClicked = (event: RowClickedEvent) => {
    if (gridRef.current?.api && !isFocusedCellActions(event)) {
      const selectedRow = gridRef.current.api.getSelectedRows()[0]

      if (selectedRow) {
        window.open(selectedRow.url, '_blank')?.focus()
        gridRef.current.api.deselectAll()
      }
    }
  }

  const handleToggleCreateOrEditModal = (isOpen: boolean) => () => {
    setCreateOrEditModalOpen(isOpen)

    if (!isOpen) {
      setExternalLinkToEdit(undefined)
    }
  }

  const handleToggleDeleteModal = (isOpen: boolean) => () => setIsDeleteModalOpen(isOpen)

  const handleSuccess = () => {
    setCreateOrEditModalOpen(false)
    setExternalLinkToEdit(undefined)
    refetch()
    queryClient.invalidateQueries({ queryKey: appsKeys.all })

    displayToast(`Link successfully ${externalLinkToEdit ? 'updated' : 'created'}`, 'success')
  }

  const handleFailure = (e: unknown) => {
    captureException(e)
    setCreateOrEditModalOpen(false)
    setExternalLinkToEdit(undefined)
    displayToast(`Link could not be ${externalLinkToEdit ? 'updated' : 'added'}`, 'error')
  }

  const handleEditExternalLink = (target: IExternalLink) => {
    const { external_link_id, target: name, url } = target
    setExternalLinkToEdit({ name, url })
    setExternalLinkIdToEdit(external_link_id)
    setCreateOrEditModalOpen(true)
  }

  const handleDeleteExternalLink = (target: IExternalLink) => {
    const { external_link_id } = target
    setExternalLinkToDelete(external_link_id)
    setIsDeleteModalOpen(true)
  }

  const handleRedirect = (url: string) => window.open(url, '_blank')

  const handleDeleteModalConfirm = async () => {
    setIsDeleteModalOpen(false)

    if (appKey && deviceType && externalLinkToDelete) {
      try {
        await deleteExternalLink({ appKey, deviceType: deviceType as DeviceType, externalLinkID: externalLinkToDelete })
        queryClient.invalidateQueries({ queryKey: appsKeys.all })
        displayToast('Link successfully removed', 'success')
        refetch()
      } catch {
        displayToast('Link could not be removed', 'error')
      }
    }
  }

  /** Render */

  const appSelectorApp = appDetails ? transformByDeviceType(appDetails, deviceType) : undefined

  const isSwitchDisabled = !appDetails?.android || !appDetails?.ios

  return (
    <>
      <Header apps={appSelectorApp ? [appSelectorApp] : []} isSwitchDisabled={isSwitchDisabled} />
      <Layout className="external-links-view" withPadding>
        <ViewTitle className="layout-view-title">App external links</ViewTitle>
        <ContentLayout loading={isLoading}>
          <TableWrapper
            addButton
            addButtonLabel="New link"
            className="links-table with-min-height"
            columnDefs={columns}
            context={{
              handleDeleteExternalLink,
              handleEditExternalLink,
              handleRedirect,
            }}
            entriesName="links"
            onAddButtonClick={handleToggleCreateOrEditModal(true)}
            onRowClicked={handleRowClicked}
            ref={gridRef}
            rowData={externalLinks}
            rowStyle={{ cursor: 'default' }}
            tableRef={tableRef}
            withPagination={false}
            withSearchBar
          />
        </ContentLayout>
      </Layout>
      {appKey && deviceType ? (
        <ManageExternalLinkModal
          appKey={appKey}
          deviceType={deviceType}
          externalLinkID={externalLinkIdToEdit}
          initialValues={externalLinkToEdit}
          isOpen={isCreateOrEditModalOpen}
          onClose={handleToggleCreateOrEditModal(false)}
          onFailure={handleFailure}
          onSuccess={handleSuccess}
        />
      ) : null}
      <ModalDialog
        isOpen={isDeleteModalOpen}
        mainText="Do you really want to delete this external link?"
        onClose={handleToggleDeleteModal(false)}
        onConfirm={handleDeleteModalConfirm}
      />
    </>
  )
}

export default memo(ExternalLinks)
